import { HttpBackend, HttpClient, HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { Translation } from '../i18n/translation.service';
import { normalizeStoredLanguage, readStoredLanguage } from '../i18n/language-storage';
import { appEnv } from '../config/app-env';
import { extractTokenPairFromApiBody } from './api-token-body.util';

const AUTH_RETRY_HEADER = 'X-Auth-Retry';

const NO_REFRESH_URL_PARTS: readonly string[] = [
  'admin/AdminAccount/login',
  'admin/AdminAccount/refresh',
  'api/Account/Login',
  'api/Account/RefreshToken',
];

let refreshInFlight: Promise<{ accessToken: string; refreshToken: string }> | null = null;

function apiBase(): string {
  return appEnv.apiBaseUrl.replace(/\/+$/, '');
}

function isUnauthorized(err: HttpErrorResponse): boolean {
  if (err.status === 401) {
    return true;
  }
  const e = err.error;
  if (e && typeof e === 'object' && 'statusCode' in e) {
    const code = (e as { statusCode?: number }).statusCode;
    return code === 401;
  }
  return false;
}

function shouldSkipRefresh(req: HttpRequest<unknown>): boolean {
  if (req.headers.has(AUTH_RETRY_HEADER)) {
    return true;
  }
  const url = req.url;
  return NO_REFRESH_URL_PARTS.some((part) => url.includes(part));
}

function refreshSession(
  plainHttp: HttpClient,
  auth: AuthService,
  acceptLanguage: string,
): Promise<{ accessToken: string; refreshToken: string }> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const ctx = auth.getRefreshContext();
      if (!ctx) {
        auth.clear();
        throw new Error('Missing refresh context');
      }
      const raw = await firstValueFrom(
        plainHttp.post<unknown>(
          `${apiBase()}/admin/AdminAccount/refresh`,
          { userId: ctx.userId, refreshToken: ctx.refreshToken },
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'Accept-Language': acceptLanguage,
            },
          },
        ),
      );
      const pair = extractTokenPairFromApiBody(raw);
      if (!pair) {
        auth.clear();
        throw new Error('Invalid refresh response');
      }
      auth.setSession(pair);
      return pair;
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export const adminAuthRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const backend = inject(HttpBackend);
  const translation = inject(Translation);
  const router = inject(Router);
  const plainHttp = new HttpClient(backend);

  const stored = readStoredLanguage();
  const fromTranslate =
    translation.translateService.getCurrentLang() || translation.translateService.getFallbackLang() || 'en-US';
  const acceptLanguage = normalizeStoredLanguage(stored ?? fromTranslate);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse)) {
        return throwError(() => err);
      }
      if (shouldSkipRefresh(req)) {
        return throwError(() => err);
      }
      const hasRefresh = !!auth.getRefreshContext();
      if (!isUnauthorized(err) || !hasRefresh) {
        if (err.status === 401) {
          auth.clear();
          void router.navigateByUrl('/login');
        }
        return throwError(() => err);
      }
      return from(refreshSession(plainHttp, auth, acceptLanguage)).pipe(
        switchMap(() => {
          const access = auth.getAccessToken();
          if (!access) {
            auth.clear();
            void router.navigateByUrl('/login');
            return throwError(() => err);
          }
          const retry = req.clone({
            setHeaders: {
              Authorization: `Bearer ${access}`,
              [AUTH_RETRY_HEADER]: '1',
            },
          });
          return next(retry);
        }),
        catchError(() => {
          auth.clear();
          void router.navigateByUrl('/login');
          return throwError(() => err);
        }),
      );
    }),
  );
};
